import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Mail as MailIcon, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { BUSINESS, BOOKING_URL } from "@/config";

const contactBlocks = [
  {
    icon: Phone,
    label: "Call",
    value: BUSINESS.phone,
    href: BUSINESS.phoneHref,
    note: "Available for 24/7 emergency call-outs",
  },
  {
    icon: Mail,
    label: "Email",
    value: BUSINESS.email,
    href: BUSINESS.emailHref,
    note: "We aim to reply within one working day",
  },
  {
    icon: MapPin,
    label: "Based In",
    value: "Edinburgh, Scotland",
    note: "Serving Edinburgh and the surrounding Lothians",
  },
];

const hours = [
  ["Monday – Friday", "8:00 – 16:30"],
  ["Saturday", "Emergency only"],
  ["Sunday", "Emergency only"],
  ["Emergencies", "24 / 7"],
];

const ContactPage = () => {
  return (
    <>
      <PageHeader
        tag="/ Contact"
        title="GET IN"
        accent="TOUCH."
        subtitle="Reach Conan directly by phone or email, or send an enquiry online — we'll get back to you as soon as possible."
      />

      <section className="relative py-16 md:py-20 bg-[#0B132B]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Booking CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="relative bg-[#151D33] border border-[#2C374D] rounded-md p-8 md:p-10 overflow-hidden mb-14"
            data-testid="contact-booking-card"
          >
            <div className="absolute top-0 left-0 w-12 h-px bg-[#D47A4A]" aria-hidden />
            <div className="absolute top-0 left-0 w-px h-12 bg-[#D47A4A]" aria-hidden />
            <div className="absolute bottom-0 right-0 w-12 h-px bg-[#D47A4A]" aria-hidden />
            <div className="absolute bottom-0 right-0 w-px h-12 bg-[#D47A4A]" aria-hidden />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
              <div className="md:col-span-7">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D47A4A] mb-3">
                  Online Booking
                </p>
                <h2 className="font-display text-3xl md:text-4xl text-white leading-tight tracking-tight">
                  BOOK A JOB IN UNDER A MINUTE.
                </h2>
                <p className="mt-4 text-sm md:text-base text-[#A0ABC0] max-w-lg">
                  Submit your enquiry through our secure booking system and
                  we'll confirm your appointment as quickly as possible.
                </p>
              </div>
              <div className="md:col-span-5 flex md:justify-end">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex w-full md:w-auto items-center justify-center gap-3 bg-[#D47A4A] hover:bg-[#B85D2A] text-[#0B132B] font-bold px-8 py-4 rounded-md uppercase tracking-[0.18em] text-sm md:text-base shadow-lg shadow-[#D47A4A]/25 hover:shadow-[#D47A4A]/50 hover:-translate-y-0.5 transition-all"
                >
                  <MailIcon className="w-5 h-5" />
                  Enquire Now
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact blocks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-14">
            {contactBlocks.map((b, i) => {
              const Icon = b.icon;
              const inner = (
                <>
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D47A4A]/10 border border-[#D47A4A]/30 rounded-md mb-5">
                    <Icon className="w-5 h-5 text-[#D47A4A]" />
                  </div>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#A0ABC0]">
                    {b.label}
                  </p>
                  <p className="font-display text-base md:text-lg text-white tracking-wide mt-2 break-words">
                    {b.value}
                  </p>
                  <p className="mt-3 text-xs md:text-sm text-[#A0ABC0]">
                    {b.note}
                  </p>
                </>
              );
              const className =
                "bg-[#151D33] border border-[#2C374D] hover:border-[#D47A4A]/60 transition-colors p-6 md:p-7 rounded-md block";
              return b.href ? (
                <a
                  key={b.label}
                  href={b.href}
                  className={`group ${className}`}
                  data-testid={`contact-block-${b.label.toLowerCase()}`}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={b.label}
                  className={className}
                  data-testid={`contact-block-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>

          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            className="bg-[#151D33] border border-[#2C374D] rounded-md p-6 md:p-10"
            data-testid="contact-hours"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-5 h-5 text-[#D47A4A]" />
              <h3 className="font-display text-xl md:text-2xl text-white tracking-wide">
                OPENING HOURS
              </h3>
            </div>
            <ul className="divide-y divide-[#2C374D]">
              {hours.map(([day, time]) => (
                <li
                  key={day}
                  className="flex items-center justify-between py-3 text-sm md:text-base"
                >
                  <span className="text-[#A0ABC0] uppercase tracking-widest text-xs md:text-sm">
                    {day}
                  </span>
                  <span
                    className={`font-display tracking-wide ${
                      day === "Emergencies" ? "text-[#D47A4A]" : "text-white"
                    }`}
                  >
                    {time}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
